import React, { useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import Badge, { KingpinBadgeType } from 'kingpin/atoms/Badge';

import { AgentEvalContext } from './AgentEvalProvider';
import { splitByApplicability, summariseEval } from './summariseEval';

// A one-glance verdict on this account's checks, in the header.

const PillButton = styled.button`
  border: 0;
  background: transparent;
  cursor: pointer;
  padding: 0;
`;

const EvalSummaryPill = () => {
  const navigate = useNavigate();
  const { checks } = useContext(AgentEvalContext);

  const { badgeType, text } = useMemo((): {
    badgeType: KingpinBadgeType;
    text: string;
  } => {
    const { applicable } = splitByApplicability(checks);
    const summary = summariseEval({ checks: applicable });
    if (summary.total === 0) {
      return { badgeType: 'Default', text: 'No checks' };
    }
    if (summary.failing > 0) {
      return { badgeType: 'Red', text: `${summary.failing} failing` };
    }
    if (summary.drifted > 0) {
      return { badgeType: 'Yellow', text: `${summary.drifted} changed` };
    }
    if (summary.passing > 0) {
      return { badgeType: 'Green', text: `${summary.passing} green` };
    }
    return { badgeType: 'Default', text: `${summary.total} not run` };
  }, [checks]);

  return (
    <PillButton
      type="button"
      onClick={() => navigate('checks')}
      data-testid="agent-eval-summary-pill"
    >
      <Badge badgeType={badgeType} text={text} />
    </PillButton>
  );
};

export default EvalSummaryPill;
